"use client";

import Link from "next/link";
import Image from "next/image";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import {
  Menu,
  Search,
  MessageCircle,
  AtSign,
  FileText,
  Maximize2,
  Bell,
  Settings,
  User,
  X,
  Trash2,
  Eye,
  Download,
  Edit,
  LogOut,
  UserCircle,
  Shield,
  Palette,
  Globe,
  Moon,
  Volume2,
  Contact,
  UserCheck,
  IndianRupee,
} from "lucide-react";
import { useAuth } from "@/store/auth";
import { useLogout } from "@/hooks/useAuth";
import { toast } from "react-toastify";
import { FullPageSpinner } from "@/components/common/FullPageSpinner";

export default function Header() {
  const router = useRouter();
  const { token, logout } = useAuth();
  const logoutMutation = useLogout();
  const [isLoading, setIsLoading] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const [activePopup, setActivePopup] = useState(null);
  const isLoggedIn = useAuth((s) => s.isLoggedIn);
  const user = useAuth.getState().user;
  useEffect(() => {
    if (!isLoggedIn) {
      router.replace("/login");
    } else {
      setHasMounted(true);
    }
  }, [isLoggedIn, router]);

  if (!hasMounted) return null;

  const handleLogout = async () => {
    setIsLoading(true);
    try {
      logoutMutation.mutate(undefined, {
        onSuccess: (res: any) => {
          toast.success("Logged out successfully!", {
            position: "top-right",
            autoClose: 5000,
          });
          logout();
          router.push("/login");
        },
        onError: (err: any) => {
          const errMsg =
            err?.response?.data?.responseData?.response?.message ||
            "Logout failed";
          toast.error(errMsg, {
            position: "top-right",
            autoClose: 5000,
          });
        },
        onSettled: () => {
          setIsLoading(false);
        },
      });
    } catch (err) {
      console.error("Logout error:", err);
      setIsLoading(false);
    }
  };

  const togglePopup = (popupName: any) => {
    setActivePopup(activePopup === popupName ? null : popupName);
  };

  const closePopup = () => setActivePopup(null);

  const notifications = [
    {
      id: 1,
      message: "New message from John Doe",
      time: "2 min ago",
      unread: true,
    },
    {
      id: 2,
      message: "Your report is ready for download",
      time: "15 min ago",
      unread: true,
    },
    {
      id: 3,
      message: "Meeting reminder: Team standup at 3 PM",
      time: "1 hour ago",
      unread: false,
    },
    {
      id: 4,
      message: "System update completed successfully",
      time: "2 hours ago",
      unread: false,
    },
  ];

  const messages = [
    {
      id: 1,
      sender: "Alice Johnson",
      message: "Hey, can we discuss the project timeline?",
      time: "5 min ago",
      avatar: "AJ",
    },
    {
      id: 2,
      sender: "Bob Smith",
      message: "The client approved the design mockups",
      time: "20 min ago",
      avatar: "BS",
    },
    {
      id: 3,
      sender: "Carol Davis",
      message: "Don't forget about tomorrow's presentation",
      time: "1 hour ago",
      avatar: "CD",
    },
  ];

  const mentions = [
    {
      id: 1,
      content: "@you in Project Alpha discussion",
      author: "Mike Wilson",
      time: "10 min ago",
    },
    {
      id: 2,
      content: "@you mentioned in Design Review",
      author: "Sarah Lee",
      time: "45 min ago",
    },
    {
      id: 3,
      content: "@you tagged in Budget Report",
      author: "David Chen",
      time: "2 hours ago",
    },
  ];

  const documents = [
    {
      id: 1,
      name: "Q4 Financial Report.pdf",
      size: "2.4 MB",
      modified: "Today, 2:30 PM",
      type: "pdf",
    },
    {
      id: 2,
      name: "Project Proposal.docx",
      size: "1.8 MB",
      modified: "Yesterday, 4:15 PM",
      type: "doc",
    },
    {
      id: 3,
      name: "Marketing Strategy.xlsx",
      size: "890 KB",
      modified: "Dec 1, 2024",
      type: "excel",
    },
    {
      id: 4,
      name: "Team Photo.jpg",
      size: "3.2 MB",
      modified: "Nov 28, 2024",
      type: "image",
    },
  ];

  return (
    <div className="relative">
      {isLoading && <FullPageSpinner />}

      <header className="bg-gradient-to-r from-cyan-400 to-blue-500 px-9 py-1">
        <div className="flex items-center justify-between">
          {/* Left section - Menu and Search */}
          <div className="flex items-center space-x-4 flex-1">
            {/* Hamburger Menu */}
            {/* <button 
              onClick={() => togglePopup('menu')}
              className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
            > */}
            {/* <Menu size={13} /> */}
            {/* </button> */}

            {/* Search Bar */}
            <div className="relative flex-1 max-w-sm">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                {/* <Search size={18} className="text-gray-400" /> */}
              </div>
              {/* <input
                type="text"
                placeholder="Search"
                className="w-full bg-white rounded-lg py-2 pl-10 pr-4 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all"
              /> */}
            </div>
          </div>

          {/* Right section - Action buttons */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => togglePopup("contact")}
              className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors relative"
            >
              <Contact size={13} />
            </button>
            {/* Messages */}
            <button
              onClick={() => togglePopup("messages")}
              className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors relative"
            >
              <MessageCircle size={13} />
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-3 w-5 flex items-center justify-center">
                3
              </span>
            </button>

            {/* Mentions */}
            <button
              onClick={() => togglePopup("mentions")}
              className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors relative"
            >
              <AtSign size={13} />
              <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full h-3 w-5 flex items-center justify-center">
                3
              </span>
            </button>

            {/* Documents */}
            <button
              onClick={() => togglePopup("documents")}
              className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
            >
              <FileText size={13} />
            </button>

            {/* Maximize */}
            <button
              onClick={() => {
                if (document.fullscreenElement) {
                  document.exitFullscreen();
                } else {
                  document.documentElement.requestFullscreen();
                }
              }}
              className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
            >
              <Maximize2 size={13} />
            </button>

            {/* Notifications */}
            <button
              onClick={() => togglePopup("notifications")}
              className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors relative"
            >
              <Bell size={13} />
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-3 w-5 flex items-center justify-center">
                2
              </span>
            </button>

            {/* Profile */}
            <button
              onClick={() => togglePopup("profile")}
              className="hover:bg-white/20 p-1 rounded-lg transition-colors"
            >
              <div className="w-5 h-5 bg-amber-600 rounded-full flex items-center justify-center">
                <User size={13} className="text-white" />
              </div>
            </button>

            {/* Settings */}
            <button
              onClick={() => togglePopup("settings")}
              className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
            >
              <Settings size={13} />
            </button>
          </div>
        </div>
      </header>

      {/* Overlay */}
      {activePopup && (
        <div className="fixed inset-0 bg-black/20 z-40" onClick={closePopup} />
      )}

      {/* Menu Popup */}
      {activePopup === "menu" && (
        <div className="absolute top-16 left-4 bg-white rounded-lg shadow-lg w-64 z-50">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800">Navigation</h3>
              <button
                onClick={closePopup}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={13} />
              </button>
            </div>
            <nav className="space-y-2">
              <Link
                href="/dashboard"
                className="block px-3 py-2 text-gray-700 hover:bg-gray-100 rounded"
              >
                Dashboard
              </Link>
              <Link
                href="/projects"
                className="block px-3 py-2 text-gray-700 hover:bg-gray-100 rounded"
              >
                Projects
              </Link>
              <Link
                href="/team"
                className="block px-3 py-2 text-gray-700 hover:bg-gray-100 rounded"
              >
                Team
              </Link>
              <Link
                href="/reports"
                className="block px-3 py-2 text-gray-700 hover:bg-gray-100 rounded"
              >
                Reports
              </Link>
              <Link
                href="/analytics"
                className="block px-3 py-2 text-gray-700 hover:bg-gray-100 rounded"
              >
                Analytics
              </Link>
            </nav>
          </div>
        </div>
      )}

      {/* Messages Popup */}
      {activePopup === "messages" && (
        <div className="absolute top-16 right-4 bg-white rounded-lg shadow-lg w-80 z-50">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800 text-[14px]">Messages</h3>
              <button
                onClick={closePopup}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={13} />
              </button>
            </div>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className="flex items-start space-x-3 p-2 hover:bg-gray-50 rounded"
                >
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-[12px] font-medium">
                    {msg.avatar}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 text-[12px]">
                      {msg.sender}
                    </p>
                    <p className="text-gray-600 truncate text-[12px]">
                      {msg.message}
                    </p>
                    <p className="text-gray-400 text-[10px]">{msg.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Messages Popup */}
      {activePopup === "contact" && (
        <div className="absolute top-16 right-4 bg-white rounded-lg shadow-lg w-60 z-50">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800">Messages</h3>
              <button
                onClick={closePopup}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={13} />
              </button>
            </div>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              <div className="space-y-1">
                <button className="w-full flex items-center space-x-3 px-3 py-2 text-gray-700 hover:bg-gray-100 rounded text-[12px]">
                  <Contact size={13} />
                  <span onClick={() => router.push("/contact")}>
                    Contact Us
                  </span>
                </button>
                <button className="w-full flex items-center space-x-3 px-3 py-2 text-gray-700 hover:bg-gray-100 rounded text-[12px]">
                  <UserCheck size={13} />
                  <span>Relationship Manager</span>
                </button>
                <button className="w-full flex items-center space-x-3 px-3 py-2 text-gray-700 hover:bg-gray-100 rounded text-[12px]">
                  <IndianRupee size={13} />
                  <span>Make Payment</span>
                </button>
                <button className="w-full flex items-center space-x-3 px-3 py-2 text-gray-700 hover:bg-gray-100 rounded text-[12px]">
                  <MessageCircle size={13} />
                  <span>Talk To Us</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mentions Popup */}
      {activePopup === "mentions" && (
        <div className="absolute top-16 right-4 bg-white rounded-lg shadow-lg w-80 z-50">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800 text-[14px]">Mentions</h3>
              <button
                onClick={closePopup}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={13} />
              </button>
            </div>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {mentions.map((mention) => (
                <div
                  key={mention.id}
                  className="p-3 border-l-4 border-orange-400 bg-orange-50 rounded"
                >
                  <p className="text-gray-800 text-[12px]">{mention.content}</p>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-gray-600 text-[12px]">
                      by {mention.author}
                    </span>
                    <span className="text-gray-400 text-[10px]">
                      {mention.time}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Documents Popup */}
      {activePopup === "documents" && (
        <div className="absolute top-16 right-4 bg-white rounded-lg shadow-lg w-96 z-50">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800 text-[14px]">Recent Documents</h3>
              <button
                onClick={closePopup}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={13} />
              </button>
            </div>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-3 hover:bg-gray-50 rounded"
                >
                  <div className="flex items-center space-x-3">
                    <FileText size={13} className="text-gray-400" />
                    <div>
                      <p className="font-medium text-gray-900 text-[12px]">
                        {doc.name}
                      </p>
                      <p className="text-gray-500 text-[10px]">
                        {doc.size} • {doc.modified}
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-1">
                    <button className="p-1 text-gray-400 hover:text-blue-600">
                      <Eye size={13} />
                    </button>
                    <button className="p-1 text-gray-400 hover:text-green-600">
                      <Download size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Notifications Popup */}
      {activePopup === "notifications" && (
        <div className="absolute top-16 right-4 bg-white rounded-lg shadow-lg w-80 z-50">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800 text-[14px]">Notifications</h3>
              <button
                onClick={closePopup}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={13} />
              </button>
            </div>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-3 rounded ${
                    notification.unread
                      ? "bg-blue-50 border-l-4 border-blue-400"
                      : "bg-gray-50"
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <p className="text-gray-800 text-[12px] flex-1">
                      {notification.message}
                    </p>
                    <button className="text-gray-400 hover:text-red-600 ml-2">
                      <Trash2 size={13} />
                    </button>
                  </div>
                  <p className="text-gray-500 text-[10px] mt-1">
                    {notification.time}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Profile Popup */}
      {activePopup === "profile" && (
        <div className="absolute top-16 right-4 bg-white rounded-lg shadow-lg w-64 z-50">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800 text-[14px]">Account</h3>
              <button
                onClick={closePopup}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={13} />
              </button>
            </div>
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-2">
                <User size={24} className="text-white" />
              </div>
              <h4 className="font-medium text-gray-900 text-[12px]">{user?.first_name}</h4>
              <p className="text-gray-500 text-[12px]">{user?.email}</p>
            </div>
            <div className="space-y-1">
              <Link href={'/profile'} >
              <button className="text-[12px] w-full flex items-center space-x-3 px-3 py-2 text-gray-700 hover:bg-gray-100 rounded">
                <UserCircle size={13} />
                <span>Profile Settings</span>
              </button>
              </Link>
              <button className="w-full flex items-center space-x-3 px-3 py-2 text-gray-700 hover:bg-gray-100 rounded text-[12px]">
                <Shield size={13} />
                <span>Privacy</span>
              </button>
              <Link
                href="/profile"
                className="flex items-center gap-2 text-gray-800 font-light hover:text-[#0e8ded]"
              >
                <button className="w-full flex items-center space-x-3 px-3 py-2 text-gray-700 hover:bg-gray-100 rounded text-[12px]">
                  <Edit size={13} />
                  <span>Edit Profile</span>
                </button>
              </Link>
              <hr className="my-2" />
              <button
                onClick={handleLogout}
                className="w-full flex items-center space-x-3 px-3 py-2 text-red-600 hover:bg-red-50 rounded text-[12px]"
              >
                <LogOut size={13} />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Popup */}
      {activePopup === "settings" && (
        <div className="absolute top-16 right-4 bg-white rounded-lg shadow-lg w-72 z-50">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800">Settings</h3>
              <button
                onClick={closePopup}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={13} />
              </button>
            </div>
            <div className="space-y-1">
              <button className="w-full flex items-center space-x-3 px-3 py-2 text-gray-700 hover:bg-gray-100 rounded">
                <Palette size={13} />
                <span>Appearance</span>
              </button>
              <button className="w-full flex items-center space-x-3 px-3 py-2 text-gray-700 hover:bg-gray-100 rounded">
                <Moon size={13} />
                <span>Dark Mode</span>
              </button>
              <button className="w-full flex items-center space-x-3 px-3 py-2 text-gray-700 hover:bg-gray-100 rounded">
                <Volume2 size={13} />
                <span>Sound Settings</span>
              </button>
              <button className="w-full flex items-center space-x-3 px-3 py-2 text-gray-700 hover:bg-gray-100 rounded">
                <Globe size={13} />
                <span>Language</span>
              </button>
              <button className="w-full flex items-center space-x-3 px-3 py-2 text-gray-700 hover:bg-gray-100 rounded">
                <Bell size={13} />
                <span>Notification Settings</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
